/**
 * SJP shared theme — generalized from MBO's mboTheme.
 *
 * Visual tokens inspired by SJ design language; reimplemented locally for
 * license cleanliness — see ADR-008. No `@sj-ab/*` imports.
 *
 * primary.main is set to #007729 (SJ dark-green) for WCAG 2.1 AA contrast
 * (5.72:1 with #fff). This preserves the BUG-005 contrast fix.
 */
import { createTheme } from "@mui/material/styles";

import {
  darkPalette,
  lightPalette,
  shape,
  spacing,
  typography,
} from "./tokens";

export const sjpTheme = createTheme({
  cssVariables: true,
  colorSchemes: {
    light: {
      palette: {
        primary: lightPalette.primary,
        secondary: lightPalette.secondary,
        error: lightPalette.error,
        warning: lightPalette.warning,
        info: lightPalette.info,
        success: lightPalette.success,
        background: lightPalette.background,
        text: lightPalette.text,
      },
    },
    dark: {
      palette: {
        primary: darkPalette.primary,
        secondary: darkPalette.secondary,
        error: darkPalette.error,
        warning: darkPalette.warning,
        info: darkPalette.info,
        success: darkPalette.success,
        background: darkPalette.background,
        text: darkPalette.text,
      },
    },
  },
  typography: {
    fontFamily: typography.fontFamily,
    fontSize: 14,
    h1: {
      fontFamily: typography.fontFamilyBold,
      fontWeight: 400,
      fontSize: "2rem",
      lineHeight: 1.125,
      fontFeatureSettings: typography.fontFeatureH1,
    },
    h2: {
      fontFamily: typography.fontFamilyBold,
      fontWeight: 400,
      fontSize: "1.5rem",
      lineHeight: 1.333,
      fontFeatureSettings: typography.fontFeature,
    },
    h3: {
      fontFamily: typography.fontFamilyBold,
      fontWeight: 400,
      fontSize: "1.1875rem",
      lineHeight: 1.474,
      fontFeatureSettings: typography.fontFeature,
    },
    h4: {
      fontFamily: typography.fontFamilyMedium,
      fontWeight: 400,
      fontSize: "1.125rem",
      lineHeight: 1.556,
      fontFeatureSettings: typography.fontFeature,
    },
    subtitle1: {
      fontFamily: typography.fontFamilyMedium,
      fontWeight: 400,
      fontSize: "1.125rem",
      lineHeight: 1.556,
      fontFeatureSettings: typography.fontFeature,
    },
    body1: {
      fontFamily: typography.fontFamily,
      fontWeight: 400,
      fontSize: "1rem",
      lineHeight: 1.5,
      fontFeatureSettings: typography.fontFeature,
    },
    caption: {
      fontFamily: typography.fontFamily,
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: 1.429,
      fontFeatureSettings: typography.fontFeature,
    },
    overline: {
      fontFamily: typography.fontFamily,
      fontWeight: 400,
      fontSize: "0.75rem",
      lineHeight: 1.333,
      fontFeatureSettings: typography.fontFeature,
      textTransform: "none",
    },
  },
  spacing,
  shape,
  components: {
    // Preserve sentence-case labels (SJ design language) — MUI defaults to uppercase.
    // Preserve SJ-style larger button typography (SJ uses ~1.125rem with medium weight,
    // MUI default is 0.875rem with letter-spacing — this restores visual parity for
    // the "+ New Case", form submit, and transition buttons.
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontFamily: typography.fontFamilyMedium,
          fontSize: "1.125rem",
          lineHeight: 1.5555555555555556,
          letterSpacing: "normal",
        },
        sizeSmall: {
          fontSize: "1rem",
          lineHeight: 1.5,
        },
        sizeLarge: {
          fontFamily: typography.fontFamilyBold,
          fontSize: "1.1875rem",
          lineHeight: 1.4736842105263157,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: "none" },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: { textTransform: "none" },
      },
    },
  },
});
